import { BoxBlue100, Gray65 } from '@box/blueprint-web-assets/tokens/tokens';
import React, { useEffect } from 'react';
import { createTheme } from 'box-ui-elements/es/utils/createTheme';
import colorLib from 'color';
import type { ThemeStyleProps } from './types';

const BuieThemeStyle = ({ wrapperClass, color }: ThemeStyleProps) => {
  useEffect(() => {
    if (color) {
      const styleEl = document.createElement('style');
      styleEl.id = 'file-request-branding-style';
      styleEl.setAttribute('data-testid', 'buie-theme');
      styleEl.appendChild(document.createTextNode(''));
      const { primary = {} } = createTheme(`#${color}`);
      const secondaryColorHex = '#222';
      const secondaryColor = colorLib(secondaryColorHex);

      const isNonBoxBlue =
        `#${color}`.toLowerCase() !== BoxBlue100.toLowerCase();

      styleEl.innerHTML = `@media all {
        .${wrapperClass} .btn-primary {
          background-color: ${primary.buttonBackground};
          border-color: ${primary.buttonBackground};
          color: ${primary.buttonForeground};
        }
        .${wrapperClass} .btn-primary:not(.bdl-is-disabled):hover, 
        .${wrapperClass} .btn-primary:not(.is-disabled):hover {
          background-color: ${primary.buttonBackgroundHover};
          border-color: ${primary.buttonBorderHover};
        }
        .${wrapperClass} .btn-primary:not(.bdl-is-disabled):active,
        .${wrapperClass} .btn-primary:not(.is-disabled):active,
        .${wrapperClass} .btn-primary:not(.bdl-is-disabled):focus,
        .${wrapperClass} .btn-primary:not(.is-disabled):focus {
          background-color: ${primary.buttonBackgroundActive};
          border-color: ${primary.buttonBorderActive};
        }
  
        .${wrapperClass} .btn-primary-hollow {
          border-color: ${primary.buttonBorder};
          color: ${primary.buttonBorder};
        }
        .${wrapperClass} .btn-primary-hollow .fill-color {
          fill: ${primary.buttonBorder};
        }
        .${wrapperClass} .crawler div {
            background-color: ${secondaryColor.string()};
        }
        .${wrapperClass} .select-container select:focus ~ .select-overlay {
            border-color: ${secondaryColor.string()};
        }
        .${wrapperClass} .content-input-section .select-container .select-button:focus {
            border: 1px solid ${secondaryColor.string()};
        }
  
        .${wrapperClass} input[type="text"]:focus,
        .${wrapperClass} input[type="password"]:focus,
        .${wrapperClass} input[type="search"]:focus,
        .${wrapperClass} input[type="email"]:focus,
        .${wrapperClass} input[type="url"]:focus,
        .${wrapperClass} input[type="tel"]:focus,
        .${wrapperClass} input[type="number"]:focus,
        .${wrapperClass} div[contentEditable="true"]:focus,
        .${wrapperClass} textarea:focus {
            border-color: ${secondaryColor.string()};
        }
        .pika-button:hover,
        .is-selected .pika-button {
            background-color: ${secondaryColor.string()};
        }
        .pika-table th {
            color: ${secondaryColor.string()};
        }
        
        .${wrapperClass} .bcu-overall-progress-bar {
           background-color: ${secondaryColor.string()};
        }
  
        .${wrapperClass} .bcu-overall-progress-bar:hover {
           background-color: ${secondaryColor.lighten(0.2).string()};
        }
        .${wrapperClass} .dropzone {
          background-color: #fbfbfb;
          border-color: ${secondaryColor.string()};
        }
        .${wrapperClass} .dropzone .btn{
          color: ${secondaryColor.string()};
          border-color: #bcbcbc;
          background-color: #ffffff;
        }
        .${wrapperClass} .dropzone .upload-illustration + div {
          color: ${secondaryColor.string()};
        }
        .${wrapperClass} .dropzone .upload-illustration .upload-illustration-fill {
          fill: #4e4e4e;
        }
        .${wrapperClass} .dropzone .upload-illustration .upload-illustration-fill-2 {
          fill: #e8e8e8;
        }
        .${wrapperClass} .dropzone .upload-illustration .upload-illustration-stroke {
          stroke: #4e4e4e;
        }
        .${wrapperClass} {
          /* Button branding tokens for blueprint-web */
          --text-text-brand-on-color: ${primary.buttonForeground};
          --surface-surface-brand: ${primary.buttonBackground};
          --surface-surface-brand-hover: ${primary.buttonBackgroundHover};
          --surface-surface-brand-pressed: ${primary.buttonBackgroundActive};
          /* BUIE Theme has no token for disabled and busy button. Setting to default TODO: Confirm with designers */
          --surface-surface-brand-disabled: ${primary.buttonBackground};
          --surface-surface-brand-busy: ${primary.buttonBackground};
            
          /* Nav branding tokens */
          --surface-nav-surface-brand-selected-focus: ${
            primary.backgroundActive
          };
          --surface-nav-surface-brand-selected: ${primary.backgroundActive};
          --surface-nav-surface-brand-focus: ${primary.backgroundActive};
          --surface-nav-surface-brand-hover: ${primary.backgroundHover};
          --surface-nav-surface-brand: ${primary.background};
          
          /* Illustrations branding tokens */
          --surface-illustration-surface-box-neutral: ${
            isNonBoxBlue ? Gray65 : BoxBlue100
          }
        }
      }`;

      if (document.body) {
        document.body.appendChild(styleEl);
      }

      return () => {
        if (styleEl.parentNode) {
          styleEl.parentNode.removeChild(styleEl);
        }
      };
    }
    return undefined;
  }, [color, wrapperClass]);

  return <></>;
};

export default BuieThemeStyle;
